import { useIonViewWillEnter } from '@ionic/react';
import Highcharts, { Chart, Options } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  ReactElement,
  ReactNode,
  RefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUpdate } from 'react-use';
import {
  Chart as BeaconChartGql,
  ChartData,
} from '../../api/graphql/generated';
import { useDarkMode } from '../../theme/dark-mode.hook';
import {
  clearChartSeries,
  formatChartSeriesData,
  redrawChart,
  upsertChartSeriesData,
} from './chart-utils';
import { ChartUiFields } from './configure-chart';
import { highChartsDarkTheme } from './highchart-options/highcharts-dark-theme';
import { highChartsLightTheme } from './highchart-options/highcharts-light-theme';

interface BeaconChartProps {
  data?: ChartData;
  chart?: Partial<BeaconChartGql>;
  renderNoResults?: ReactNode;
}

export interface BeaconChartRef {
  clearChart: () => void;
}

const BeaconChart = forwardRef<BeaconChartRef, BeaconChartProps>(
  (props: BeaconChartProps, ref): ReactElement => {
    const { data, chart, renderNoResults } = props;

    const update = useUpdate();
    const chartUiFields = useMemo<ChartUiFields>(() => {
      return chart?.fields?.ui;
    }, [chart]);

    const chartRef = useRef<{
      chart: Chart;
      container: RefObject<HTMLDivElement>;
    }>(null);

    useImperativeHandle(ref, (): BeaconChartRef => {
      return {
        clearChart: () => {
          clearChartSeries(chartRef.current?.chart);
        },
      };
    });

    const isDarkTheme = useDarkMode();
    const [options, setOptions] = useState<Options>(
      getHighChartOptions(isDarkTheme ? 'dark' : 'light')
    );

    const setChartSize = (w: number | null = null, h: number | null = null) => {
      requestAnimationFrame(() => {
        chartRef.current?.chart.setSize(w, h, false);
      });
    };

    useIonViewWillEnter(() => {
      setChartSize();
      // forcing a re-render when the view comes back so that when changing
      // certain chart settings (hideZeroValues), the component is forced to
      // re-render
      update();
    });

    setChartSize();

    const allSeriesData = formatChartSeriesData(data, chartUiFields);

    // clear existing data before updating with new data
    clearChartSeries(chartRef.current?.chart);
    for (const index in allSeriesData) {
      const series = allSeriesData[index];
      upsertChartSeriesData(chartRef.current?.chart, series);
    }
    redrawChart(chartRef.current?.chart);

    useEffect(() => {
      setOptions(getHighChartOptions(isDarkTheme ? 'dark' : 'light'));
    }, [isDarkTheme]);

    const showNoResults = data?.results.length === 0 && renderNoResults;

    return (
      <>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          containerProps={{
            style: {
              height: '100%',
              display: showNoResults ? 'none' : undefined,
            },
          }}
          options={options}
        />
        {showNoResults && renderNoResults}
      </>
    );
  }
);

BeaconChart.displayName = 'BeaconChart';

const getHighChartOptions = (theme: 'dark' | 'light'): Options => {
  if (theme === 'dark') {
    return highChartsDarkTheme();
  } else {
    return highChartsLightTheme();
  }
};

export { BeaconChart };
